/**
 * Locale-aware number and date formatting, modelled on next-intl's `useFormatter`
 * (`format.number`, `format.dateTime`) so a move to next-intl only swaps the import.
 * Output follows the Czech locale: non-breaking spaces between thousands and
 * before the unit, a decimal comma, and dates as `07.10.2026`.
 *
 * @see https://next-intl.dev/docs/usage/numbers
 * @see https://next-intl.dev/docs/usage/dates-times
 */
import dayjs from 'dayjs';

/** Named `Intl` options, referenced by name in `format.number` and `format.dateTime`. */
export type Formats = {
  number?: Record<string, Intl.NumberFormatOptions>;
  dateTime?: Record<string, Intl.DateTimeFormatOptions>;
};

/** Locale of every user-facing number and date in the app. */
export const DEFAULT_LOCALE = 'cs-CZ';

/**
 * The app's named formats.
 *
 * - `currency`: CZK, decimals only when there are cents: `8 000 Kč`, `1 234,50 Kč`
 * - `amount`: like `currency` without the symbol, for markup that renders `Kč`
 *   separately: `8 000`, `1 234,50`
 * - `percent`: a value that is already in percent (`9.5`, not `0.095`): `9,5 %`
 * - `decimal`: a plain number with up to two decimals, for rates whose `%` the
 *   copy or the markup renders: `9,5`
 * - `date`: `07.10.2026`
 */
export const formats = {
  number: {
    currency: {
      style: 'currency',
      currency: 'CZK',
      trailingZeroDisplay: 'stripIfInteger',
    },
    amount: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      trailingZeroDisplay: 'stripIfInteger',
    },
    percent: { style: 'unit', unit: 'percent', maximumFractionDigits: 2 },
    decimal: { maximumFractionDigits: 2 },
  },
  dateTime: {
    date: { day: '2-digit', month: '2-digit', year: 'numeric' },
  },
} satisfies Formats;

function resolveOptions<O extends object>(
  kind: keyof Formats,
  namedFormats: Record<string, O> | undefined,
  formatOrOptions: string | number | symbol | O,
  overrides: O | undefined,
): O {
  const options =
    typeof formatOrOptions === 'object'
      ? formatOrOptions
      : namedFormats?.[formatOrOptions as string];

  if (!options) {
    throw new Error(`Unknown ${kind} format "${String(formatOrOptions)}"`);
  }

  return { ...options, ...overrides };
}

/**
 * Creates a formatter bound to a locale and a set of named formats.
 * The app uses the shared {@link format}; create your own for another locale or in tests.
 */
export function createFormatter<F extends Formats>({
  locale,
  formats: namedFormats,
}: {
  locale: string;
  formats?: F;
}) {
  return {
    /**
     * Formats a number with a named format or with `Intl.NumberFormat` options.
     * Options in `overrides` are applied on top of a named format.
     * Returns `''` for `undefined`, `null` and `NaN`, so data that is still loading renders nothing.
     *
     * @throws when `formatOrOptions` names a format that does not exist
     *
     * @example
     * format.number(1234.5, 'currency'); // '1 234,50 Kč'
     * format.number(8000, 'currency', { trailingZeroDisplay: 'auto' }); // '8 000,00 Kč'
     * format.number(12.345, { maximumFractionDigits: 1 }); // '12,3'
     */
    number(
      value: number | null | undefined,
      formatOrOptions: keyof F['number'] | Intl.NumberFormatOptions,
      overrides?: Intl.NumberFormatOptions,
    ): string {
      if (value === undefined || value === null || Number.isNaN(value)) {
        return '';
      }

      const options = resolveOptions(
        'number',
        namedFormats?.number,
        formatOrOptions,
        overrides,
      );

      return new Intl.NumberFormat(locale, options).format(value);
    },

    /**
     * Formats a date string with a named format. A date-only string such as
     * `'2026-10-07'` is read as local midnight, so it shows the same day in every time zone.
     * Numeric dates have no space after the dots (`07.10.2026`).
     * Returns `''` for a missing or invalid date.
     *
     * @example
     * format.dateTime('2026-10-07', 'date'); // '07.10.2026'
     */
    dateTime(
      value: string | null | undefined,
      formatOrOptions: keyof F['dateTime'] | Intl.DateTimeFormatOptions,
      overrides?: Intl.DateTimeFormatOptions,
    ): string {
      // `dayjs(undefined)` is "now", so a missing date must be caught first
      const date = value ? dayjs(value) : undefined;

      if (!date?.isValid()) {
        return '';
      }

      const options = resolveOptions(
        'dateTime',
        namedFormats?.dateTime,
        formatOrOptions,
        overrides,
      );

      // dayjs reads a date-only string ('2026-10-07') as local midnight, so the
      // calendar day does not shift with the time zone like `new Date()` does
      const formatted = new Intl.DateTimeFormat(locale, options).format(
        date.toDate(),
      );

      // Numeric dates are written without a space after the dots
      // (cs-CZ gives '07. 10. 2026'); a written-out month keeps it ('7. října')
      return formatted.replace(/\.\s(?=\d)/g, '.');
    },
  };
}

/** The app's formatter: Czech locale with the named {@link formats}. */
export const format = createFormatter({ locale: DEFAULT_LOCALE, formats });
